/**
 * Zod runtime validators for the Datacrate Model.
 *
 * Used at the load + persist boundaries (Firestore + GCS) — a corrupt
 * DAM should NEVER reach the renderer. The loader catches a parse
 * failure, logs it, and falls through to the inline-build fallback so
 * report generation never breaks on a broken DAM.
 *
 * Every schema below mirrors its TypeScript counterpart in
 * `datacrateModel.ts` 1:1. The companion file is the human-facing
 * doc; this file is the machine-facing runtime guard.
 *
 * Drift detection: `datacrateModel.schema.test.ts` round-trips a
 * synthetic DAM through both type + schema. Adding a field to the
 * interface without adding it to the schema (or vice versa) fails
 * that test.
 */
import { z } from 'zod';
import { REPORT_TYPE_REGISTRY } from '../config/reportTypes';
import type { ReportType } from './datacrateModel';

// The DataSource schema mirrors the shape in `types/siteRag.ts`. We
// don't import a zod schema from there (siteRag has no zod surface
// today); duplicating the shape here is intentional — DataSource is
// the only foreign type the DAM references at the boundary, and the
// duplication is loud enough to catch with a typecheck if it drifts.
const dataSourceSchema = z.object({
  id: z.string().optional(),
  siteId: z.string().optional(),
  field: z.string(),
  source: z.string(),
  url: z.string().optional(),
  retrievedAt: z.string(),
  sourceGrade: z
    .enum([
      'verified-professional',
      'authoritative-government',
      'reviewed-landgate',
      'regional-screening',
      'inference',
    ])
    .optional(),
  dataAsOf: z.string().optional(),
  qualification: z
    .enum(['verified', 'screening', 'estimated', 'unknown', 'unavailable'])
    .optional(),
  status: z.string().optional(),
  available: z.boolean().optional(),
  missing: z.boolean().optional(),
  state: z.string().optional(),
  reviewState: z.string().optional(),
  coverageStatus: z.string().optional(),
  notes: z.string().optional(),
  summary: z.string().optional(),
  description: z.string().optional(),
  excerpt: z.string().optional(),
  excerptPreview: z.string().optional(),
  sourceQuote: z.string().optional(),
  rationale: z.string().optional(),
  evidenceQuotes: z.array(z.string()).optional(),
  attribution: z
    .object({
      statedBy: z.string().optional(),
      statedByRole: z.string().optional(),
      organisation: z.string().optional(),
      statedOn: z.string().optional(),
      appliesToProject: z.string().optional(),
      projectName: z.string().optional(),
    })
    .optional(),
  demonstrationDisclosure: z
    .object({
      schemaVersion: z.literal(1),
      kind: z.literal('fictional-scenario'),
      scenarioId: z.string(),
      notice: z.literal('FICTIONAL DEMONSTRATION — NOT FOR REAL-WORLD DECISIONS'),
    })
    .optional(),
  documentId: z.string().optional(),
  pageNumber: z.number().int().positive().optional(),
  topographicSupport: z
    .object({
      measuredSlopePct: z.number().finite(),
      topographicContours: z.boolean().optional(),
      contourIntervalFt: z.number().finite().nonnegative().optional(),
      contourIntervalM: z.number().finite().nonnegative().optional(),
      surveyPointsSampled: z.number().int().nonnegative().optional(),
      derivationMethod: z
        .enum([
          'field-topographic-survey',
          'alta-table-a-item-5-contours',
          'lidar-tin-grade',
          'civil-grading-survey',
        ])
        .optional(),
      siteId: z.string().optional(),
    })
    .optional(),
  subItems: z
    .array(
      z.object({
        topic: z.string(),
        detail: z.string(),
      }),
    )
    .optional(),
  citedInThisReport: z.boolean().optional(),
});

// Literal tuple of every ReportType — used to build a typed Zod enum
// whose inferred type is the narrow `ReportType` union, NOT a widened
// `string`. The runtime assertion below catches drift between this
// tuple and `REPORT_TYPE_REGISTRY` at module load time: add a new
// report type without updating this tuple and the module fails to
// import (build break, intentional).
const REPORT_TYPE_TUPLE = [
  'feasibility',
  'environmental',
  'test-fit',
  'power-availability',
  'water-resource',
  'interconnection',
  // PSR-1 (row 0.99290) — Public Sentiment Report. The drift assertion
  // below pins this tuple against REPORT_TYPE_REGISTRY.
  'public-sentiment',
  'supply-chain-risk',
  'custom-brief',
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report.
  'financial',
  'executive-summary',
] as const satisfies readonly ReportType[];

{
  const registryKeys = Object.keys(REPORT_TYPE_REGISTRY).sort();
  const tupleKeys = [...REPORT_TYPE_TUPLE].sort();
  if (
    registryKeys.length !== tupleKeys.length ||
    registryKeys.some((k, i) => k !== tupleKeys[i])
  ) {
    throw new Error(
      `[datacrateModel.schema] REPORT_TYPE_TUPLE drift — tuple ${JSON.stringify(tupleKeys)} ≠ registry ${JSON.stringify(registryKeys)}. Update the tuple to match config/reportTypes.ts.`,
    );
  }
}

export const reportTypeSchema = z.enum(REPORT_TYPE_TUPLE);

export const generatedBySchema = z.enum([
  'cron',
  'admin-manual',
  'source-change-hook',
  'on-demand-fallback',
]);

export const regulatoryFrameworkSchema = z.object({
  id: z.string(),
  name: z.string(),
  jurisdiction: z.string(),
  applicableWhen: z.string(),
  keyCitations: z.array(z.string()),
  decisionImpact: z.enum(['gating', 'material', 'informational']),
  summary: z.string(),
});

export const analyticalQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  category: z.string(),
  mustAnswer: z.boolean(),
  derivedFromSedSections: z.array(z.string()),
  promptHint: z.string(),
});

export const observedPatternSchema = z.object({
  pattern: z.string(),
  evidenceCitations: z.array(z.string()),
  applicabilityScore: z.number().min(0).max(1),
});

export const riskFactorSchema = z.object({
  id: z.string(),
  description: z.string(),
  triggers: z.array(z.string()),
  severity: z.enum(['critical', 'major', 'moderate', 'minor']),
  mitigations: z.array(z.string()),
  citations: z.array(z.string()),
});

export const normalizedBenchmarkSchema = z.object({
  metric: z.string(),
  value: z.object({
    low: z.number(),
    typical: z.number(),
    high: z.number(),
    unit: z.string(),
  }),
  source: z.string(),
  citationRef: z.string(),
  vintage: z.number().int(),
});

export const decisionCriterionSchema = z.object({
  criterion: z.string(),
  weight: z.number().min(0).max(1),
  threshold: z.object({
    pass: z.number(),
    conditional: z.number(),
    fail: z.number(),
  }),
  dataRequirement: z.string(),
});

export const glossaryTermSchema = z.object({
  term: z.string(),
  expansion: z.string(),
  definition: z.string().optional(),
});

export const damSourceManifestSchema = z.object({
  peerReviewDbHash: z.string(),
  peerReviewEntriesUsed: z.array(z.string()),
  industryBenchmarksHash: z.string(),
  regulatoryDbHash: z.string(),
  adamsCuratedAccessions: z.array(z.string()),
  archetypeTemplateHashes: z.record(z.string(), z.string()),
  acronymsHash: z.string(),
  documentChecklistsHash: z.string(),
  liveLiteratureSnapshot: z
    .object({
      fetchedAt: z.string(),
      providersUsed: z.array(z.enum(['crossref', 'arxiv', 'pubmed'])),
      entryIds: z.array(z.string()),
    })
    .optional(),
});

export const damVerificationSchema = z.object({
  claimsExtracted: z.number().int().min(0),
  claimsVerified: z.number().int().min(0),
  unverifiedClaims: z.array(z.string()),
  verificationPassedAt: z.string(),
  verifierModel: z.string(),
});

/** TEL-03 — synthesis model provenance; optional so older DAMs still parse. */
export const damSynthesisInfoSchema = z.object({
  modelUsed: z.string().min(1),
  location: z.string().min(1).optional(),
  promptTemplateHash: z.string().min(1).optional(),
});

export const damQualitySignalsSchema = z.object({
  deduplicationRate: z.number().min(0).max(1),
  coverageBySection: z.record(z.string(), z.number().min(0).max(1)),
  diversityScore: z.number().min(0).max(1),
});

/**
 * DCO Wave-B — `DamSlotEntry` runtime validator. Optional array on the
 * DAM root (`slotEntries?: DamSlotEntry[]`). See `damToSlot.ts` for the
 * reader.
 */
export const damSlotEntrySchema = z.object({
  id: z.string().min(1),
  reportType: reportTypeSchema,
  sectionId: z.string().min(1),
  fieldKey: z.string().min(1),
  /** Optional authored-schema bridge — when set, DCO stamps
   *  `provenanceDetail.autoFillFields[schemaKey]` on apply. */
  requirementId: z.string().min(1).optional(),
  schemaKey: z.string().min(1).optional(),
  geoScope: z.string().min(1),
  // `unknown` so the schema doesn't constrain SED payload shapes — the
  // reader's caller is responsible for shape-validation at the boundary.
  value: z.unknown(),
  confidence: z.number().min(0).max(1),
  citations: z.array(z.string()).optional(),
  rationale: z.string().max(500).optional(),
});

export const datacrateModelSchema = z.object({
  reportType: reportTypeSchema,
  version: z.number().int().positive(),
  schemaVersion: z.number().int().positive(),
  generatedAt: z.string(),
  generatedBy: generatedBySchema,
  buildDurationMs: z.number().min(0),
  buildSeed: z.string(),
  sourceManifest: damSourceManifestSchema,
  summary: z.string(),
  frameworks: z.array(regulatoryFrameworkSchema),
  benchmarks: z.array(normalizedBenchmarkSchema),
  keyQuestions: z.array(analyticalQuestionSchema),
  commonPatterns: z.array(observedPatternSchema),
  criticalRisks: z.array(riskFactorSchema),
  glossary: z.array(glossaryTermSchema),
  decisionFramework: z.array(decisionCriterionSchema),
  references: z.array(dataSourceSchema),
  renderedContext: z.string(),
  renderedContextChars: z.number().int().min(0),
  renderedContextTokenEstimate: z.number().int().min(0),
  verification: damVerificationSchema,
  /** TEL-03 — must be declared: z.object strips unknown keys, so an
   *  undeclared `synthesis` would be dropped by safeParseDam on load. */
  synthesis: damSynthesisInfoSchema.optional(),
  qualitySignals: damQualitySignalsSchema,
  /** CU6 — optional per-archetype default prefs. Older DAMs lack this. */
  defaultPrefsByArchetype: z
    .record(z.string(), z.record(z.string(), z.unknown()))
    .optional(),
  /** DCO Wave-B — optional curated slot entries the orchestrator reads
   *  from. Older DAMs lack this; `applyDamToSlot` treats `undefined`
   *  the same as an empty array (no DAM coverage).
   *
   *  DDS — DD-3 originally added a duplicate `damSlotEntrySchema` with
   *  an older shape (`slotGeoScopeSchema` union + `citationRef` +
   *  `notes`); the rebase drops the duplicate and keeps main's
   *  canonical-shape schema. See pack
   *  `docs/prompt-packs/Dam_Slot_Entry_Reconciliation_Prompt.md`. */
  slotEntries: z.array(damSlotEntrySchema).optional(),
});

/**
 * Inferred type — useful for round-trip tests that want to assert the
 * zod schema produces a shape assignable to `DatacrateModel`. The
 * two MUST stay structurally compatible; the drift test enforces it.
 */
export type DatacrateModelFromSchema = z.infer<typeof datacrateModelSchema>;

/**
 * Convenience: parse with helpful error context. Returns `{ ok: true,
 * value }` on success, `{ ok: false, errors }` on failure — never
 * throws. Storage callers turn `ok: false` into a non-blocking warn
 * (the inline-build fallback covers the report generation).
 */
export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; errors: string[] };

export function safeParseDam(value: unknown): ParseResult<DatacrateModelFromSchema> {
  const parsed = datacrateModelSchema.safeParse(value);
  if (parsed.success) return { ok: true, value: parsed.data };
  const errors = parsed.error.issues.map(
    (issue) => `${issue.path.join('.') || '$'}: ${issue.message}`,
  );
  return { ok: false, errors };
}
